/**
 * Letture trasversali sui set accessibili (ADR 0032): task di tutti i set e
 * proposte, scadenze del calendario, siti pubblicati, consumo per progetto.
 *
 * Il backend filtra: ogni lettura riguarda i set dell'utente e quelli di cui è
 * membro (tutti per l'`ADMIN`). La pagina mostra ciò che riceve.
 *
 * Provvisorio dichiarato, come gli altri client: percorsi scritti a mano invece che
 * generati dagli schemi OpenAPI. `work.test.ts` presidia metodo, percorso e query.
 */

import type { ApiClient } from './client';
import type { TaskGroup, TaskKind, TaskPriority } from './tasks/taskTypes';

/** Un task di un set accessibile. */
export interface CrossTask {
  /** Task. */
  id: string;
  /** Chiave leggibile (`WEB-12`). */
  key: string;
  /** Set. */
  set_id: string;
  /** Nome del set. */
  set_name: string;
  /** Progetto. */
  project_id: string;
  /** Nome del progetto nel set. */
  project_name: string;
  /** Titolo. */
  title: string;
  /** Tipo di lavoro. */
  kind: TaskKind;
  /** Priorità. */
  priority: TaskPriority;
  /** Gruppo dello stato. */
  state_group: TaskGroup;
  /** Nome dello stato. */
  state_name: string;
  /** `agent_role`, `user` o vuoto. */
  assignee_kind: '' | 'agent_role' | 'user';
  /** Ruolo o subject. */
  assignee: string;
  /** Nome dell'assegnatario persona, se noto. */
  assignee_name: string;
  /** Scadenza, ISO 8601. */
  due_at: string | null;
  /** Creazione, ISO 8601. */
  created_at: string;
  /** Ultima modifica, ISO 8601. */
  updated_at: string;
}

/** Ordine dei task di tutti i set. */
export type CrossTaskSort = 'updated' | 'due' | 'priority' | 'created';

/** Filtri dei task di tutti i set. */
export interface CrossTaskFilters {
  /** Solo questo set. */
  setId?: string;
  /** Gruppi di stato. */
  groups?: readonly TaskGroup[];
  /** Tipi. */
  kinds?: readonly TaskKind[];
  /** Priorità. */
  priorities?: readonly TaskPriority[];
  /** True per i soli task assegnati al chiamante. */
  mine?: boolean;
  /** Testo nel titolo. */
  q?: string;
  /** Ordine. */
  sort?: CrossTaskSort;
  /** Task da saltare. */
  offset?: number;
  /** Task della pagina, al più 50. */
  limit?: number;
}

/** Una pagina dei task di tutti i set. */
export interface CrossTaskPage {
  /** Task. */
  items: CrossTask[];
  /** Task che rispondono ai filtri. */
  total: number;
  /** Offset della pagina dopo; `null` all'ultima. */
  next_offset: number | null;
}

/** Proposte da approvare di tutti i set. */
export interface CrossProposals {
  /** Proposte, dalla più recente (al più 100). */
  items: CrossTask[];
  /** Proposte in tutto. */
  total: number;
}

/** Task con scadenza in un intervallo. */
export interface DueTasks {
  /** Task, per scadenza (al più 500). */
  items: CrossTask[];
  /** True se nell'intervallo ce n'erano di più. */
  truncated: boolean;
}

/** Un sito pubblicato. */
export interface PublishedSite {
  /** Set. */
  set_id: string;
  /** Nome del set. */
  set_name: string;
  /** Progetto. */
  project_id: string;
  /** Nome del progetto. */
  project_name: string;
  /** Indirizzo. */
  url: string;
  /** `awake`, `asleep` o `waking`. */
  sleep_state: string;
}

/** Consumo di un progetto nel mese. */
export interface ProjectUsageItem {
  /** Progetto; `null` per i messaggi scritti per tutti i progetti. */
  project_id: string | null;
  /** Nome del progetto; vuoto per «tutti i progetti» o un progetto tolto. */
  project_name: string;
  /** Token conteggiati. */
  tokens: number;
  /** Messaggi con consumo. */
  requests: number;
}

/** Consumo per progetto di un set (`GET …/usage/projects`). */
export interface ProjectUsage {
  /** Mese UTC, `AAAA-MM`. */
  month: string;
  /** Una voce per progetto, dal più alto. */
  items: ProjectUsageItem[];
  /** Somma. */
  total: number;
}

/** Chiave dei messaggi «tutti i progetti» nel dettaglio per giorno. */
export const ALL_PROJECTS_KEY = 'all';

/**
 * Query string dei filtri dei task di tutti i set.
 *
 * @param filters Filtri.
 * @returns La query, senza `?`.
 */
export function crossTaskQuery(filters: CrossTaskFilters): string {
  const params = new URLSearchParams();
  if (filters.setId !== undefined && filters.setId !== '') {
    params.set('set_id', filters.setId);
  }
  for (const group of filters.groups ?? []) {
    params.append('group', group);
  }
  for (const kind of filters.kinds ?? []) {
    params.append('kind', kind);
  }
  for (const priority of filters.priorities ?? []) {
    params.append('priority', priority);
  }
  if (filters.mine === true) {
    params.set('assignee', '@me');
  }
  const text = filters.q?.trim() ?? '';
  if (text !== '') {
    params.set('q', text);
  }
  if (filters.sort !== undefined) {
    params.set('sort', filters.sort);
  }
  if (filters.limit !== undefined) {
    params.set('limit', String(filters.limit));
  }
  if (filters.offset !== undefined && filters.offset > 0) {
    params.set('offset', String(filters.offset));
  }
  return params.toString();
}

/** Client delle letture trasversali. */
export class WorkApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Una pagina dei task di tutti i set accessibili.
   *
   * @param filters Filtri e pagina.
   * @returns La pagina.
   */
  listTasks(filters: CrossTaskFilters): Promise<CrossTaskPage> {
    const query = crossTaskQuery(filters);
    return this.client.request<CrossTaskPage>('GET', `/api/v1/agent-sets/tasks${query === '' ? '' : `?${query}`}`);
  }

  /**
   * Proposte da approvare di tutti i set accessibili.
   *
   * @returns Le proposte e il totale.
   */
  proposals(): Promise<CrossProposals> {
    return this.client.request<CrossProposals>('GET', '/api/v1/agent-sets/tasks/proposals');
  }

  /**
   * Task con scadenza nell'intervallo.
   *
   * @param from Inizio, ISO 8601 (incluso).
   * @param to Fine, ISO 8601 (escluso); al più 100 giorni dopo.
   * @returns I task.
   */
  dueTasks(from: string, to: string): Promise<DueTasks> {
    const params = new URLSearchParams({ from, to });
    return this.client.request<DueTasks>('GET', `/api/v1/agent-sets/tasks/due?${params.toString()}`);
  }

  /**
   * Siti pronti dei set accessibili.
   *
   * @returns I siti.
   */
  async sites(): Promise<PublishedSite[]> {
    const body = await this.client.request<{ items: PublishedSite[] }>('GET', '/api/v1/agent-sets/sites');
    return body.items;
  }

  /**
   * Consumo per progetto di un set nel mese.
   *
   * @param setId Set.
   * @param month Mese `AAAA-MM`; omesso, il corrente.
   * @returns Il consumo.
   */
  projectUsage(setId: string, month?: string): Promise<ProjectUsage> {
    const query = month === undefined ? '' : `?${new URLSearchParams({ month }).toString()}`;
    return this.client.request<ProjectUsage>(
      'GET',
      `/api/v1/agent-sets/${encodeURIComponent(setId)}/usage/projects${query}`,
    );
  }
}
